"use client";

import { useTranslations } from "next-intl";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth-store";
import type { InvoiceStatus } from "@/types";

interface StatusBadgeProps {
  status: InvoiceStatus;
  className?: string;
  /**
   * Phase 67 D-12 — layered amber OVERDUE badge rendered to the RIGHT of
   * the status badge when truthy. Driven by Invoice.is_overdue (derived
   * server-side from due_date < today AND status IN (sent, partial)).
   * InvoiceStatus enum is unchanged — overdue is a derived flag, not a
   * new state (Phase 67 D-13).
   */
  isOverdue?: boolean;
}

/**
 * Phase 64 D-14 / UI-SPEC §Status badges.
 *
 * Exhaustive over all 5 invoice statuses:
 *   draft   → neutral gray
 *   sent    → blue
 *   partial → amber
 *   paid    → green (emerald)
 *   void    → strikethrough red
 *
 * Reuses the existing shadcn Badge primitive with the `outline` variant
 * stripped of its default border via the ring system, so the color tokens
 * read consistently across light/dark theme.
 *
 * Phase 67 extension: optional isOverdue prop renders a second amber
 * OVERDUE badge to the right (UI-SPEC §Overdue indicator).
 */
const VARIANT_CLASS: Record<InvoiceStatus, string> = {
  draft:
    "bg-muted text-muted-foreground ring-muted-foreground/20",
  sent:
    "bg-blue-100 text-blue-900 ring-blue-200/50 dark:bg-blue-950 dark:text-blue-200",
  partial:
    "bg-amber-100 text-amber-900 ring-amber-200/50 dark:bg-amber-950 dark:text-amber-200",
  paid:
    "bg-emerald-100 text-emerald-900 ring-emerald-200/50 dark:bg-emerald-950 dark:text-emerald-200",
  void:
    "bg-red-100 text-red-900 ring-red-200/50 line-through dark:bg-red-950 dark:text-red-200",
};

// Static-literal status pill resolver — T-72-01 (never `t(statusVar)`).
//
// simple_clinic_mode (walk-in clinics, e.g. albdental) never "sends" an
// invoice — the patient pays at the desk — so `sent` reads as "Unpaid" there.
// Both branches are static literals.
function statusBadgeLabel(
  status: InvoiceStatus,
  t: (key: string) => string,
  simpleClinicMode: boolean,
): string {
  switch (status) {
    case "draft":
      return t("statusBadge.draft");
    case "sent":
      return simpleClinicMode
        ? t("statusBadge.sentSimple")
        : t("statusBadge.sent");
    case "partial":
      return t("statusBadge.partial");
    case "paid":
      return t("statusBadge.paid");
    case "void":
      return t("statusBadge.void");
    default:
      return status;
  }
}

export function StatusBadge({ status, className, isOverdue }: StatusBadgeProps) {
  const t = useTranslations("invoices");
  const simpleClinicMode = useAuthStore(
    (s) => s.user?.feature_flags?.simple_clinic_mode ?? false,
  );
  const statusBadge = (
    <Badge
      variant="outline"
      className={cn(
        "ring-1 ring-inset border-transparent",
        VARIANT_CLASS[status] ?? VARIANT_CLASS.draft,
        className,
      )}
    >
      {statusBadgeLabel(status, t, simpleClinicMode)}
    </Badge>
  );

  if (!isOverdue) {
    return statusBadge;
  }

  return (
    <span className="inline-flex items-center gap-1.5">
      {statusBadge}
      <Badge
        variant="outline"
        className="ring-1 ring-inset border-transparent bg-amber-100 text-amber-900 ring-amber-300/60 dark:bg-amber-900/40 dark:text-amber-200"
      >
        {t("statusBadge.overdue")}
      </Badge>
    </span>
  );
}
